/**
 * ══════════════════════════════════════════════════════════════════════════════
 * PTEL DESIGN SYSTEM v2.0 - TYPESCRIPT TOKENS
 * ══════════════════════════════════════════════════════════════════════════════
 * 
 * Constantes TypeScript sincronizadas con tokens.css v2.
 * Basado en ptel-design-system-v1.0-completo_20260103.zip
 * 
 * Scores: Progresión semafórica Lime → Amber → Orange → Red
 * Stepper: Teal uniforme
 * 
 * Uso:
 *   import { colors, typography, getScoreClass } from '@/design-system/v2';
 * 
 * ══════════════════════════════════════════════════════════════════════════════
 */

// ─────────────────────────────────────────────────────────────────────────────
// COLORES
// ─────────────────────────────────────────────────────────────────────────────

export const colors = {
  // Fondos
  bg: {
    primary: '#262624',
    secondary: '#2d2d2a',
    tertiary: '#353532',
    hover: '#3a3a37',
    overlay: '#1e1e1c', // tooltips y capas flotantes sobre bg.primary
    subtle: 'rgba(255, 255, 255, 0.05)', // fondo neutro para estados idle/pending
  },
  
  // Textos
  fg: {
    primary: '#fafaf9',
    secondary: '#a8a8a0',
    muted: '#71717a',
    subtle: '#52525b',
  },
  
  // Bordes
  border: {
    DEFAULT: '#3f3f3c',
    light: '#2f2f2c',
  },
  
  // Acciones
  primary: '#2dd4bf',
  primaryHover: '#5eead4',
  primaryMuted: 'rgba(45, 212, 191, 0.15)',
  onPrimary: '#0F1A18', // texto oscuro sobre fondo teal (botón activo)
  success: '#a3e635',
  successMuted: 'rgba(163, 230, 53, 0.15)',
  warning: '#fbbf24',
  warningMuted: 'rgba(251, 191, 36, 0.15)',
  error: '#f87171',
  errorMuted: 'rgba(248, 113, 113, 0.15)',
  info: '#3b82f6',
  infoMuted: 'rgba(59, 130, 246, 0.15)',

  // Acentos multipropósito UI (VIS-2b: ex-colisiones casuales con formats.*)
  accent: {
    purple: '#A78BFA', // pestañas revisar/abrev · tipología · fusionar · INE
    orange: '#FB923C', // escala local ScoreCell «Revisar» (naranja deliberado ≠ ámbar)
    // VIS-2b-ii (Bucket B, pixel-idéntico): hex huérfanos del camino vivo de P2
    amberLight: '#FCD34D', // texto destacado dentro de InfoBanner «valida» (Step2Page: rechazo, coords duplicadas)
    ochre: '#E0A85A', // celda Constr.–Reforma de la ranura Catastro (CatastroInlinePanel, C3.14 / UI-P2-CAT-01)
  },
  
  // Scores (4 niveles) - DS oficial D2: lime/blue/amber/red · umbrales 85/70/50
  scores: {
    high: '#A3E635',      // ≥85 - Lime
    medium: '#60A5FA',    // 70-84 - Blue
    low: '#FBBF24',       // 50-69 - Amber
    critical: '#F87171',  // <50 - Red
  },
  
  // Scores backgrounds
  scoresBg: {
    high: 'rgba(163, 230, 53, 0.15)',
    medium: 'rgba(96, 165, 250, 0.15)',
    low: 'rgba(251, 191, 36, 0.15)',
    critical: 'rgba(248, 113, 113, 0.15)',
  },

  // Origen de la coordenada (P1UNO-b, DOP-2 a): distintos de los de calidad
  // para que el eje ORIGEN y el eje CALIDAD no se confundan en la misma fila
  // (maqueta 3g sellada). La vía `crs` se funde en «corregida».
  vias: {
    documento: '#34D399',     // verde agua — tal como venía en el documento
    corregida: '#A78BFA',     // violeta — corregida (dígito, zona, formato o CRS)
    geocodificada: '#60A5FA', // azul — por dirección o nombre
    centroide: '#FBBF24',     // ámbar — centro del municipio; revisar
  },
  
  // Tipologías - Material Design
  typologies: {
    san: { color: '#E91E63', name: 'Sanitario', bgAlpha: 'rgba(233, 30, 99, 0.15)' },
    edu: { color: '#2196F3', name: 'Educativo', bgAlpha: 'rgba(33, 150, 243, 0.15)' },
    pol: { color: '#3F51B5', name: 'Seguridad', bgAlpha: 'rgba(63, 81, 181, 0.15)' },
    seg: { color: '#3F51B5', name: 'Seguridad', bgAlpha: 'rgba(63, 81, 181, 0.15)' },
    cul: { color: '#9C27B0', name: 'Cultural', bgAlpha: 'rgba(156, 39, 176, 0.15)' },
    rel: { color: '#795548', name: 'Religioso', bgAlpha: 'rgba(121, 85, 72, 0.15)' },
    eme: { color: '#F44336', name: 'Emergencias', bgAlpha: 'rgba(244, 67, 54, 0.15)' },
    dep: { color: '#4CAF50', name: 'Deportivo', bgAlpha: 'rgba(76, 175, 80, 0.15)' },
    adm: { color: '#607D8B', name: 'Administrativo', bgAlpha: 'rgba(96, 125, 139, 0.15)' },
    inf: { color: '#00BCD4', name: 'Infraestructura', bgAlpha: 'rgba(0, 188, 212, 0.15)' },
    via: { color: '#22C55E', name: 'Vial', bgAlpha: 'rgba(34, 197, 94, 0.15)' },
    ene: { color: '#EAB308', name: 'Energético', bgAlpha: 'rgba(234, 179, 8, 0.15)' },
    hid: { color: '#06B6D4', name: 'Hidráulico', bgAlpha: 'rgba(6, 182, 212, 0.15)' },
    otr: { color: '#6B7280', name: 'Otro', bgAlpha: 'rgba(107, 114, 128, 0.15)' },
  },
  
  // Formatos de exportación — acento visual por formato (P4 Exporta)
  formats: {
    xlsx:      '#22C55E', // verde — ofimática Microsoft
    geojson:   '#2DD4BF', // teal — alias de colors.primary (GIS open)
    csv:       '#94A3B8', // gris pizarra — tabular universal
    kml:       '#FB923C', // naranja — Google Earth
    shapefile: '#A78BFA', // púrpura — SIG profesional
    ods:       '#34D399', // verde menta — open source
    html:      '#60A5FA', // azul — web
  },

  // Pasos (stepper) - Teal uniforme
  steps: {
    color: '#2dd4bf',
    bg: 'rgba(45, 212, 191, 0.08)',
    border: 'rgba(45, 212, 191, 0.3)',
    // Info Banner mantiene identidad visual por paso
    normaliza: '#2dd4bf',
    valida: '#fbbf24',
    descarga: '#a3e635',
  },
  
  // Filas con error
  rowError: {
    bg: 'rgba(248, 113, 113, 0.04)',
    border: '#f87171',
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// COLORES - LIGHT MODE
// ─────────────────────────────────────────────────────────────────────────────

/** Tipo ampliado de colores (sin literal strings para bg/fg/border) */
type DeepString<T> = T extends string ? string : { [K in keyof T]: DeepString<T[K]> };
export type ColorsConfig = DeepString<typeof colors>;

export const lightColors: ColorsConfig = {
  // Fondos light
  bg: {
    primary: '#ffffff',
    secondary: '#f9fafb',
    tertiary: '#f3f4f6',
    hover: '#e5e7eb',
    overlay: '#f0f0ee',
    subtle: 'rgba(0, 0, 0, 0.03)',
  },

  // Textos light
  fg: {
    primary: '#18181b',
    secondary: '#71717a',
    muted: '#a1a1aa',
    subtle: '#d4d4d8',
  },

  // Bordes light
  border: {
    DEFAULT: '#e4e4e7',
    light: '#f4f4f5',
  },

  // Semánticos — idénticos al tema oscuro
  primary: colors.primary,
  primaryHover: colors.primaryHover,
  primaryMuted: colors.primaryMuted,
  onPrimary: colors.onPrimary,
  success: colors.success,
  successMuted: colors.successMuted,
  warning: colors.warning,
  warningMuted: colors.warningMuted,
  error: colors.error,
  errorMuted: colors.errorMuted,
  info: colors.info,
  infoMuted: colors.infoMuted,
  accent: { ...colors.accent }, // provisional: mismos valores; revisar cuando P2 tenga diseño light
  scores: { ...colors.scores },
  scoresBg: { ...colors.scoresBg },
  vias: { ...colors.vias }, // mismos hex en claro (P1UNO-b, DOP-2 a)
  typologies: { ...colors.typologies },
  formats: { ...colors.formats },
  steps: { ...colors.steps },
  rowError: { ...colors.rowError },
};

// ─────────────────────────────────────────────────────────────────────────────
// THEME HELPER
// ─────────────────────────────────────────────────────────────────────────────

export type ThemeMode = 'dark' | 'light';

/**
 * Devuelve el objeto de colores según el modo de tema
 */
export const getThemeColors = (mode: ThemeMode): ColorsConfig => {
  return mode === 'dark' ? colors : lightColors;
};

// ─────────────────────────────────────────────────────────────────────────────
// TIPOGRAFÍA
// ─────────────────────────────────────────────────────────────────────────────

export const typography = {
  fontFamily: {
    sans: "'Space Grotesk', system-ui, -apple-system, sans-serif",
    mono: "'JetBrains Mono', 'Fira Code', monospace",
  },
  
  fontSize: {
    '2xs': '10px',
    xs: '11px',
    sm: '12px',
    base: '13px',
    md: '14px',
    lg: '18px',
    xl: '24px',
    pageTitle: '20px', // h1 de página — fuera de escala base pero token oficial
  },
  
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
  },
  
  letterSpacing: {
    wide: '0.5px',
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// ESPACIADO (Sistema de 4px)
// ─────────────────────────────────────────────────────────────────────────────

export const spacing = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  14: '56px',  // alineado con margen horizontal de content en wizard-v2 (Step1/2/3/4 pages)
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// RADIOS DE BORDE
// ─────────────────────────────────────────────────────────────────────────────

export const borderRadius = {
  sm: '4px',
  DEFAULT: '6px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  full: '9999px',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// SOMBRAS
// ─────────────────────────────────────────────────────────────────────────────

export const shadows = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.3)',
  md: '0 4px 12px rgba(0, 0, 0, 0.3)',
  lg: '0 8px 24px rgba(0, 0, 0, 0.4)',
  xl: '0 12px 48px rgba(0, 0, 0, 0.5)',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// BREAKPOINTS
// ─────────────────────────────────────────────────────────────────────────────

export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// OPACIDADES ESTÁNDAR
// ─────────────────────────────────────────────────────────────────────────────

export const opacity = {
  badgeBg: 0.15,
  hoverSubtle: 0.08,
  active: 0.20,
  modalOverlay: 0.6,
  glassmorphism: 0.7,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// ANCHOS DE BORDE
// ─────────────────────────────────────────────────────────────────────────────

export const borderWidth = {
  DEFAULT: '1px',
  1.5: '1.5px',
  2: '2px',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// CONTENEDORES
// ─────────────────────────────────────────────────────────────────────────────

export const containers = {
  header: '1152px',
  stepper: '1024px',
  main: '896px',
  modalSm: '384px',
  modalMd: '448px',
  modalLg: '672px',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// TRANSICIONES
// ─────────────────────────────────────────────────────────────────────────────

export const transitions = {
  fast: '0.1s ease',
  base: '0.15s ease',
  slow: '0.3s ease',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Z-INDEX
// ─────────────────────────────────────────────────────────────────────────────

export const zIndex = {
  base: 0,
  dropdown: 50,
  sticky: 100,
  modal: 200,
  tooltip: 300,
  toast: 400,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// DIMENSIONES DE COMPONENTES
// ─────────────────────────────────────────────────────────────────────────────

export const components = {
  header: {
    height: '56px',
  },
  typeBadge: {
    width: '84px',
  },
  sidebar: {
    width: '280px',
  },
  content: {
    maxWidth: '1400px',
  },
  button: {
    xs: { height: '24px', paddingX: '8px', fontSize: '10px', iconSize: '12px' },
    sm: { height: '32px', paddingX: '12px', fontSize: '12px', iconSize: '14px' },
    md: { height: '40px', paddingX: '16px', fontSize: '14px', iconSize: '16px' },
    lg: { height: '48px', paddingX: '20px', fontSize: '16px', iconSize: '18px' },
  },
  input: {
    sm: { height: '32px', paddingX: '12px', paddingY: '8px', fontSize: '12px' },
    md: { height: '40px', paddingX: '12px', paddingY: '10px', fontSize: '14px' },
    lg: { height: '48px', paddingX: '16px', paddingY: '12px', fontSize: '16px' },
  },
  toast: {
    paddingX: '16px',
    paddingY: '12px',
  },
  table: {
    minWidth: '900px',
    columns: {
      index: '40px',
      infra: 'minmax(300px, 1fr)',
      map: '100px',
      type: '110px',
      x: '72px',
      utm: '28px',
      y: '78px',
      score: '100px',
    },
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────────────────────

export type ScoreClass = 'high' | 'medium' | 'low' | 'critical';
export type ScoreFilterId = 'all' | 'high' | 'medium' | 'low' | 'critical' | 'pending';
export type TypologyCode = keyof typeof colors.typologies;
export type StepName = 'normaliza' | 'valida' | 'descarga';

export interface SourceConfig {
  label: string;
  color: string;
  bgAlpha: string;
}

export interface ScoreRangeConfig {
  id: ScoreFilterId;
  label: string;
  min?: number;
  max?: number;
  isPending?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// UMBRALES DE SCORE
// ─────────────────────────────────────────────────────────────────────────────

export const SCORE_THRESHOLDS = {
  high: 85,
  medium: 70,
  low: 50,
  critical: 0,
} as const;

export const SCORE_LABELS: Record<ScoreClass, string> = {
  high: 'Excelente',
  medium: 'Aceptable',
  low: 'Revisar',
  critical: 'Crítico',
};

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURACIÓN DE FUENTES DE DATOS
// ─────────────────────────────────────────────────────────────────────────────

export const SOURCE_CONFIG: Record<string, SourceConfig> = {
  original: {
    label: 'DOC',
    color: '#A3E635',
    bgAlpha: 'rgba(163, 230, 53, 0.15)',
  },
  cartociudad: {
    label: 'IGN',
    color: '#38BDF8',
    bgAlpha: 'rgba(56, 189, 248, 0.15)',
  },
  dera: {
    label: 'DERA',
    color: '#C084FC',
    bgAlpha: 'rgba(192, 132, 252, 0.15)',
  },
  cdau: {
    label: 'CDAU',
    color: '#2DD4BF',
    bgAlpha: 'rgba(45, 212, 191, 0.15)',
  },
  manual: {
    label: 'MAN',
    color: '#FBBF24',
    bgAlpha: 'rgba(251, 191, 36, 0.15)',
  },
  pending: {
    label: '—',
    color: '#52525B',
    bgAlpha: 'rgba(82, 82, 91, 0.10)',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// RANGOS DE SCORE PARA FILTROS
// ─────────────────────────────────────────────────────────────────────────────

export const SCORE_RANGES: ScoreRangeConfig[] = [
  { id: 'all', label: 'Todos' },
  { id: 'high', label: '≥85', min: 85 },
  { id: 'medium', label: '70-84', min: 70, max: 84 },
  { id: 'low', label: '50-69', min: 50, max: 69 },
  { id: 'critical', label: '<50', min: 1, max: 49 },
  { id: 'pending', label: 'Sin coord.', isPending: true },
];

// ─────────────────────────────────────────────────────────────────────────────
// UTILIDADES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convierte color HEX a RGBA con alpha
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Obtiene la clase de score basada en el valor numérico
 */
export const getScoreClass = (score: number): ScoreClass => {
  if (score >= SCORE_THRESHOLDS.high) return 'high';
  if (score >= SCORE_THRESHOLDS.medium) return 'medium';
  if (score >= SCORE_THRESHOLDS.low) return 'low';
  return 'critical';
};

/**
 * Obtiene el color del score basado en el valor numérico
 */
export const getScoreColor = (score: number): string => {
  const scoreClass = getScoreClass(score);
  return colors.scores[scoreClass];
};

/**
 * Obtiene el label del score basado en el valor numérico
 */
export const getScoreLabel = (score: number): string => {
  const scoreClass = getScoreClass(score);
  return SCORE_LABELS[scoreClass];
};

/**
 * Obtiene el color de una tipología
 */
export const getTypologyColor = (code: TypologyCode): string => {
  return colors.typologies[code]?.color ?? colors.fg.muted;
};

/**
 * Obtiene el nombre de una tipología
 */
export const getTypologyName = (code: TypologyCode): string => {
  return colors.typologies[code]?.name ?? code.toUpperCase();
};

/**
 * Obtiene el color del paso del stepper (siempre Teal)
 */
export const getStepColor = (_step?: StepName): string => {
  return colors.steps.color;
};

/**
 * Obtiene el color del Info Banner por paso
 */
export const getInfoBannerColor = (step: StepName): string => {
  return colors.steps[step];
};

/**
 * Genera el estilo CSS para un badge de tipología
 */
export const getTypologyStyle = (code: TypologyCode): React.CSSProperties => {
  const typo = colors.typologies[code];
  if (!typo) return {};
  return {
    backgroundColor: typo.bgAlpha,
    color: typo.color,
  };
};

/**
 * Genera el estilo CSS para un badge de score
 */
export const getScoreStyle = (score: number): React.CSSProperties => {
  const scoreClass = getScoreClass(score);
  return {
    backgroundColor: colors.scoresBg[scoreClass],
    color: colors.scores[scoreClass],
  };
};

/**
 * Genera el estilo CSS para un badge de score sólido (fondo = color)
 */
export const getScoreSolidStyle = (score: number): React.CSSProperties => {
  const scoreClass = getScoreClass(score);
  return {
    backgroundColor: colors.scores[scoreClass],
    color: colors.bg.primary,
  };
};

/**
 * Obtiene la configuración de una fuente de datos
 */
export const getSourceConfig = (source: string): SourceConfig => {
  return SOURCE_CONFIG[source] ?? SOURCE_CONFIG.pending;
};

/**
 * Genera el estilo CSS para un badge de fuente
 */
export const getSourceStyle = (source: string): React.CSSProperties => {
  const cfg = getSourceConfig(source);
  return {
    backgroundColor: cfg.bgAlpha,
    color: cfg.color,
  };
};

/**
 * Obtiene color + fondo para un rango de score (filtros)
 */
export const getScoreRangeStyle = (id: ScoreFilterId): { color: string; bg: string } => {
  const map: Record<ScoreFilterId, { color: string; bg: string }> = {
    all: { color: colors.fg.secondary, bg: colors.bg.tertiary },
    high: { color: colors.scores.high, bg: colors.scoresBg.high },
    medium: { color: colors.scores.medium, bg: colors.scoresBg.medium },
    low: { color: colors.scores.low, bg: colors.scoresBg.low },
    critical: { color: colors.scores.critical, bg: colors.scoresBg.critical },
    pending: { color: colors.scores.critical, bg: colors.scoresBg.critical },
  };
  return map[id] ?? map.all;
};

/**
 * Obtiene el color de una barra de progreso según porcentaje
 */
export const getProgressColor = (percentage: number): string => {
  if (percentage >= 80) return colors.scores.high;
  if (percentage >= 50) return colors.scores.low;
  return colors.scores.critical;
};

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT DEFAULT
// ─────────────────────────────────────────────────────────────────────────────

const designSystemV2 = {
  colors,
  lightColors,
  getThemeColors,
  typography,
  spacing,
  borderRadius,
  breakpoints,
  opacity,
  borderWidth,
  containers,
  shadows,
  transitions,
  zIndex,
  components,
  SCORE_THRESHOLDS,
  SCORE_LABELS,
  SOURCE_CONFIG,
  SCORE_RANGES,
  getScoreClass,
  getScoreColor,
  getScoreLabel,
  getTypologyColor,
  getTypologyName,
  getStepColor,
  getInfoBannerColor,
  getTypologyStyle,
  getScoreStyle,
  getScoreSolidStyle,
  getSourceConfig,
  getSourceStyle,
  getScoreRangeStyle,
  getProgressColor,
  hexToRgba,
};

export default designSystemV2;
